import React, { useState, useEffect } from "react";
import { format, differenceInCalendarDays, isValid } from "date-fns";
import { es as esLocale, ru as ruLocale, enUS } from "date-fns/locale";
import { MessageCircle, CalendarIcon, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useLang } from "@/contexts/LanguageContext";
import { openWhatsApp } from "@/lib/whatsapp";
import { api } from "@/lib/api";

const LOCALES = { en: enUS, es: esLocale, ru: ruLocale };

export default function BookingModal({ open, onClose, vehicle, settings }) {
  const { lang, t } = useLang();
  const [pickup, setPickup] = useState(null);
  const [ret, setRet] = useState(null);
  const fmt = t.dateFormat || "dd/MM/yyyy";
  const locale = LOCALES[lang] || enUS;

  useEffect(() => {
    if (open) { setPickup(null); setRet(null); }
  }, [open]);

  const days = (() => {
    if (!pickup || !ret || !isValid(pickup) || !isValid(ret)) return 0;
    const d = differenceInCalendarDays(ret, pickup);
    if (d < 0) return -1;
    return d === 0 ? 1 : d;
  })();

  const invalid = days === -1;
  const hasDates = pickup && ret && days > 0;

  const handleReserve = () => {
    const pStr = pickup ? format(pickup, fmt, { locale }) : "—";
    const rStr = ret ? format(ret, fmt, { locale }) : "—";

    let message;
    if (hasDates) {
      message = t.whatsapp.simpleDates(vehicle.name, pStr, rStr);
    } else {
      message = t.whatsapp.simple(vehicle.name);
    }

    api.post("/bookings", {
      vehicleId: vehicle.id,
      vehicleName: vehicle.name,
      language: lang,
      pickupDate: pickup ? format(pickup, "yyyy-MM-dd") : "",
      returnDate: ret ? format(ret, "yyyy-MM-dd") : "",
      numberOfDays: hasDates ? days : 0,
      pickupLocation: "",
      returnLocation: "",
      totalPrice: 0,
      whatsappMessage: message,
    }).catch(() => {});

    openWhatsApp(message, settings, lang, {
      vehicleId: vehicle.id,
      vehicleName: vehicle.name,
      pickupDate: pickup ? format(pickup, "yyyy-MM-dd") : "",
      returnDate: ret ? format(ret, "yyyy-MM-dd") : "",
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid={`booking-modal-${vehicle.id}`}
        className="max-w-md w-[95vw] sm:w-full bg-[#0C0C0C] border border-[#2A2A2A] rounded-none p-0 translate-x-[-50%] translate-y-[-50%]">
        <DialogTitle className="sr-only">{t.booking.title}</DialogTitle>
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between mb-2">
            <div className="text-overline text-[#C9A55A]">{t.booking.title}</div>
            <button onClick={onClose} aria-label="Close" className="text-[#F5F1EB] hover:text-[#C9A55A]" data-testid="booking-modal-close">
              <X size={18} />
            </button>
          </div>
          <h3 className="font-display text-[#F5F1EB] text-2xl leading-tight mb-6">{vehicle.name}</h3>

          <div className="grid grid-cols-2 gap-3">
            <DateField label={t.booking.pickupDate} value={pickup} onChange={setPickup} fmt={fmt} locale={locale} t={t} testid={`m-pickup-${vehicle.id}`} />
            <DateField label={t.booking.returnDate} value={ret} onChange={setRet} fmt={fmt} locale={locale} t={t} testid={`m-return-${vehicle.id}`} disabledBefore={pickup} />
          </div>

          <div className="mt-4 text-xs text-[#8A8A8A] tracking-[0.16em] uppercase text-center">
            {invalid
              ? <span className="text-[#B8342A]">{t.booking.invalidDates}</span>
              : hasDates
                ? `${days} ${days === 1 ? t.booking.day : t.booking.days}`
                : t.catalog.selectDates}
          </div>

          <button
            onClick={handleReserve}
            disabled={invalid}
            className="btn-red w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`booking-modal-submit-${vehicle.id}`}>
            <MessageCircle size={16} /> {t.catalog.contactWa24h}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Label = ({ children }) => <label className="text-[10px] text-[#8A8A8A] tracking-[0.18em] uppercase block mb-1">{children}</label>;

function DateField({ label, value, onChange, fmt, locale, t, testid, disabledBefore }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <Label>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testid}
            className="w-full bg-[#0C0C0C] border border-[#2A2A2A] hover:border-[#C9A55A] text-left text-[#F5F1EB] px-3 h-10 text-sm flex items-center justify-between">
            <span>{value ? format(value, fmt, { locale }) : t.booking.pickDate}</span>
            <CalendarIcon size={14} className="text-[#C9A55A]" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="bg-[#0C0C0C] border border-[#2A2A2A] p-0 rounded-none w-auto z-[100]">
          <Calendar mode="single" selected={value || undefined}
            onSelect={(d) => { onChange(d || null); setOpen(false); }}
            locale={locale}
            disabled={disabledBefore ? { before: new Date(disabledBefore.getTime() + 86400000) } : undefined}
            className="text-[#F5F1EB]" />
        </PopoverContent>
      </Popover>
    </div>
  );
}
